/**
 * Created by slava on 04.10.2023
 *
 * The circle-path meta progress yatzy started with: a snap scroll of round location badges joined
 * by lines, a star counter and the location name. The aisensia alt view replaced it with a bar and
 * a cards window on 2025-06-05 (aisensia 095fd89f4); this is that view ported to the current
 * location API and plugged in through SimpleMainSceneComponent.buildProgress for yatzy only.
 */

const YatzyStarCountView = cc.Node.extend({
    ctor: function () {
        this._super();
        this.setAnchorPoint(0.5, 0.5);

        this.location = cleverapps.meta.getSelectedLocation();

        this.bg = cleverapps.UI.createScale9Sprite(bundles.simple.frames.percentage_bg);
        this.addChild(this.bg);

        this.setText();
    },

    setText: function () {
        const styles = cleverapps.styles.YatzyStarCountView;

        if (this.text) {
            this.text.removeFromParent();
        }

        const required = this.location.getRequiredProgress();
        const stars = Math.min(this.location.getProgress(), required);

        const text = this.text = new TextWithIcon(`${stars}/${required} **`, {
            font: cleverapps.styles.FONTS.YATZY_STAR_COUNT_TEXT,
            icons: {
                "**": bundles.reward_icons.frames.simple_star_png
            }
        });
        this.addChild(text);

        this.setContentSize2(text.width + 2 * styles.padding.x, text.height + 2 * styles.padding.y);
        this.bg.setContentSize2(this.getContentSize());
        this.bg.setAlignment(styles.bg);
        text.setAlignment(styles.text);
    },

    animateChange: function () {
        this.setText();

        this.runAction(new cc.Sequence(
            new cc.ScaleTo(0.15, 1.25),
            new cc.ScaleTo(0.15, 1)
        ));
    }
});

const YatzyProgressView = cc.Node.extend({
    ctor: function () {
        this._super();
        this.setAnchorPoint(0.5, 0.5);

        this.location = cleverapps.meta.getSelectedLocation();

        this.starCountView = new YatzyStarCountView();
        this.addChild(this.starCountView, 1);

        this.build();

        this.setAlignment(cleverapps.styles.YatzyProgressView.position);

        cleverapps.aims.registerTarget("locationProgress", this.starCountView, {
            controls: ["progress_view"],
            flyingAnimation: Reward.JUMP_COLLECT_ANIMATION,
            duration: 1000
        });

        this.location.on("nextStage", this.onNextStage.bind(this), this);
        this.location.on("showForce", this.showForce.bind(this), this);
        this.location.on("updateProgress", this.updateProgress.bind(this), this);
    },

    onAfterResize: function () {
        if (this.mode !== cleverapps.resolution.mode) {
            this.build();
        }
    },

    build: function () {
        const styles = cleverapps.styles.YatzyProgressView;

        this.mode = cleverapps.resolution.mode;
        const vertical = this.vertical = this.mode === cleverapps.WideMode.VERTICAL;

        [this.bgFrame1, this.bgFrame2, this.scroll, this.text].forEach((node) => node && node.removeFromParent());
        delete this.text;

        this.bgFrame1 = cleverapps.UI.createScale9Sprite(vertical ? bundles.simple.frames.frame_vertical_1 : bundles.simple.frames.frame_1);
        this.bgFrame1.setContentSize2(styles.background.size.frame1);
        this.addChild(this.bgFrame1, -1);

        this.bgFrame2 = cleverapps.UI.createScale9Sprite(vertical ? bundles.simple.frames.frame_vertical_2 : bundles.simple.frames.frame_2);
        this.bgFrame2.setContentSize2(styles.background.size.frame2);
        this.addChild(this.bgFrame2, -1);
        cleverapps.UI.onClick(this.bgFrame2, this.onClick.bind(this));

        this.createScroll();

        this.setContentSize2(this.scroll.width * this.scroll.scale, this.scroll.height * this.scroll.scale + (vertical ? styles.scroll.vertical.padding : 0));

        this.scroll.setAlignment(styles.scroll.position);
        this.bgFrame1.setAlignment(styles.background.position.frame1);
        this.bgFrame2.setAlignment(styles.background.position.frame2);
        this.starCountView.setAlignment(styles.starCount);

        this.updateText(this.location.getStage());
    },

    createScroll: function () {
        const styles = cleverapps.styles.YatzyProgressView.scroll;
        const scrollStyles = this.vertical ? styles.vertical : styles.horizontal;

        this.items = [];
        // one page per stage plus the coming-soon badge
        for (let index = 0; index <= this.location.getTotalStages(); index++) {
            const item = this.createItem(index);
            const lines = [this.createLine(index), item, this.createLine(index + 1)];
            const page = new cleverapps.Layout(this.vertical ? lines.reverse() : lines, {
                direction: this.vertical ? cleverapps.UI.VERTICAL : cleverapps.UI.HORIZONTAL,
                margin: cleverapps.styles.YatzyProgressView.line.overlap
            });
            page.item = item;
            this.items.push(page);
        }

        // a blank first page lets the first badge sit in the middle; the vertical path climbs, so it is fed bottom-up
        const pages = [this.createEmptyItem(this.items[0].getContentSize())].concat(this.items);
        const scroll = this.scroll = new SnapScrollView(this.vertical ? pages.reverse() : pages, {
            direction: this.vertical ? cleverapps.UI.ScrollView.DIR_VERTICAL : cleverapps.UI.ScrollView.DIR_HORIZONTAL,
            pagesCount: styles.pagesCount,
            width: scrollStyles.width,
            height: scrollStyles.height,
            scrollBarEnabled: false
        });

        if (scrollStyles.scale) {
            scroll.setScale(scrollStyles.scale);
        }

        scroll.onUpdatePositionListener = this.onScroll.bind(this);
        scroll.scrollTo(this.items[this.location.getStage()]);
        this.addChild(scroll);
    },

    // page offset of the scroll centre, in pages, along the scroll axis
    getScrollOffset: function () {
        const scroll = this.scroll;
        const pos = scroll.getContainerPosition();
        if (this.vertical) {
            return (pos.y + (scroll.innerContent.height - scroll.pageSize.height) / 2) / scroll.pageSize.height;
        }
        return (pos.x + (scroll.innerContent.width - scroll.pageSize.width) / 2) / scroll.pageSize.width;
    },

    onScroll: function () {
        const scroll = this.scroll;
        const zoom = cleverapps.styles.YatzyProgressView.scroll.zoom;
        const offset = this.getScrollOffset();
        const last = scroll.items.length - 1;

        const lower = cleverapps.clamp(Math.floor(offset), 0, last);
        const upper = Math.min(lower + 1, last);
        const center = cleverapps.clamp(Math.round(offset), 0, last);

        // the centred page is drawn above its neighbours
        scroll.items[center].replaceParentSamePlace(scroll.items[center].parent);

        const scaleItem = (page, scale) => {
            if (page.item && !page.item.cantScale) {
                page.item.setScale(scale);
            }
        };
        scaleItem(scroll.items[upper], (offset - lower) * (1 - zoom) + zoom);
        scaleItem(scroll.items[lower], (upper - offset) * (1 - zoom) + zoom);

        const stage = this.items.indexOf(scroll.items[center]);
        if (stage !== -1 && stage !== this.textStage) {
            this.updateText(stage);
        }
    },

    createEmptyItem: function (size) {
        const container = new cc.Node();
        container.setAnchorPoint(0.5, 0.5);
        container.setContentSize2(size);
        return container;
    },

    createLine: function (index) {
        const styles = cleverapps.styles.YatzyProgressView.line;
        const stage = this.location.getStage();
        let line = new cc.Node();

        // no line before the first badge and none after the coming-soon one
        if (index > 0 && index <= this.location.getTotalStages()) {
            const completed = index <= stage;
            if (this.vertical) {
                line = cleverapps.UI.createScale9Sprite(completed ? bundles.simple.frames.line_bg_vertical_completed : bundles.simple.frames.line_bg_vertical_uncompleted);
            } else {
                line = cleverapps.UI.createScale9Sprite(completed ? bundles.simple.frames.line_bg_completed : bundles.simple.frames.line_bg_uncompleted);
            }
        }

        line.setContentSize2(this.vertical ? styles.vertical : styles.horizontal);
        line.setLocalZOrder(-1);
        return line;
    },

    createIcon: function (index) {
        const stage = this.location.getStage();

        if (index > stage + 1) {
            return new cc.Sprite(bundles.simple.frames.lock_png);
        }

        const icon = new cc.Sprite(bundles[this.location.getIconBundle(index)].urls.icon);
        if (index === stage + 1) {
            icon.setColor(cleverapps.styles.COLORS.COLOR_DARK_GRAY);
            icon.setOpacity(125);
        }
        return icon;
    },

    createItem: function (index) {
        const styles = cleverapps.styles.YatzyProgressView.scroll;

        const container = new cc.Node();
        container.setAnchorPoint(0.5, 0.5);

        container.progressBar = this.createProgressBar(index);
        container.setContentSize2(container.progressBar.getContentSize());
        container.progressBar.setPosition(container.width / 2, container.height / 2);
        container.addChild(container.progressBar);

        if (this.location.isComingSoonStage(index)) {
            const comingSoonText = cleverapps.UI.generateOnlyText(Messages.get("Simple.ComingSoon"), cleverapps.styles.FONTS.BACKGROUNDS_WINDOW_NAME_COMING_SOON);
            comingSoonText.setHorizontalAlignment(cc.TEXT_ALIGNMENT_CENTER);
            comingSoonText.setDimensions(container.width - styles.diffX, 0);
            comingSoonText.setPosition(container.width / 2 + styles.dx, container.height / 2);
            container.addChild(comingSoonText);
        } else {
            container.icon = this.createIcon(index);
            container.icon.setPosition(container.width / 2, container.height / 2);
            container.addChild(container.icon);
        }

        container.setScale(styles.zoom);

        return container;
    },

    createProgressBar: function (index) {
        const stage = this.location.getStage();
        const progressBar = new cleverapps.Spine(bundles.simple.jsons.progress_bar_animation);

        let animation = "idle completed";
        if (index === stage) {
            animation = `idle_${this.getPercent()}`;
        } else if (index > stage) {
            animation = "idle closed";
        }

        progressBar.setAnimation(0, animation, false);
        return progressBar;
    },

    // the spine has a frame per 5 percent
    getPercent: function () {
        const percent = Math.min(1, this.location.getProgress() / this.location.getRequiredProgress()) * 100;
        return Math.floor(percent / 5) * 5;
    },

    updateProgress: function () {
        const page = this.items[this.location.getStage()];
        if (!page) {
            return;
        }

        const percent = this.getPercent();
        if (percent === 0) {
            page.item.progressBar.setAnimation(0, "idle_0", false);
        } else {
            page.item.progressBar.setAnimationAndIdleAfter(`animation_${percent}`, `idle_${percent}`);
        }

        this.starCountView.animateChange();
    },

    // the stage is completed in place; the scene rebuilds this view once the location has moved on
    onNextStage: function () {
        const page = this.items[this.location.getStage()];
        if (!page || !page.item.icon) {
            return;
        }

        const item = page.item;

        const particles = new cleverapps.Spine(bundles.simple.jsons.circle_particles_animation);
        particles.setAnimation(0, "animation", false);
        particles.setCompleteListenerRemove();
        particles.setPosition(item.icon.width / 2, item.icon.height / 2);
        cleverapps.UI.fitToBox(particles, item.icon);
        item.icon.addChild(particles, -1);
        item.icon.runAction(StandartAnimations.pulse(item.icon, {}));

        cleverapps.audio.playSound(bundles.simple.urls.simple_next_stage);

        item.runAction(new cc.Sequence(
            new cc.DelayTime(0.6),
            new cc.CallFunc(() => {
                item.cantScale = true;
                item.progressBar.setAnimationAndIdleAfter("completion animation", "completed mark");
            })
        ));
    },

    updateText: function (stage) {
        const styles = cleverapps.styles.YatzyProgressView.text;

        this.textStage = stage;

        const msg = this.location.isComingSoonStage(stage) ? "Simple.ComingSoon" : `background_name_${stage}`;
        const text = cleverapps.UI.generateOnlyText(Messages.get(msg), cleverapps.styles.FONTS.BACKGROUND_NAME_TEXT);
        text.fitTo(styles.width);
        text.setAlignment(styles.position);
        this.addChild(text);

        if (this.text) {
            const oldText = this.text;
            oldText.runAction(StandartAnimations.disappear(oldText)).setFinalize(() => {
                oldText.removeFromParent();
            });
            text.runAction(StandartAnimations.showUp(text));
            cleverapps.audio.playSound(bundles.simple.urls.slide);
        }

        this.text = text;
    },

    onClick: function () {
        if (cleverapps.forces.isRunningForce()) {
            cleverapps.forces.closeRunningForce();
        }
        cleverapps.effects.fire("ui.button.click");
        cleverapps.effects.fire("ui.button.haptic");

        cleverapps.focusManager.display({
            focus: "BackgroundsAltWindow",
            action: (f) => {
                new BackgroundsAltWindow();
                cleverapps.focusManager.onceNoWindowsListener = f;
            }
        });
    },

    showForce: function () {
        cleverapps.focusManager.setEventNodes([this]);
        FingerView.hintTap(this.bgFrame2, {
            runOnce: true
        });
    }
});

SimpleMainSceneComponent.prototype.buildProgress = function () {
    return new YatzyProgressView();
};

// every badge up to the next stage shows its icon, so those icon bundles load with the scene
SimpleMainSceneComponent.prototype.listBundles = cleverapps.extendFunc(SimpleMainSceneComponent.prototype.listBundles, function (styling) {
    const res = this._super(styling);
    const location = cleverapps.meta.getSelectedLocation();

    for (let stage = 0; stage <= location.getStage() + 1; stage++) {
        res.push(location.getIconBundle(stage));
    }

    return cleverapps.unique(res);
});

cleverapps.overrideFonts(cleverapps.styles.FONTS, {
    YATZY_STAR_COUNT_TEXT: {
        name: "default",
        size: 31
    }
});

cleverapps.styles.YatzyStarCountView = {
    padding: {
        x: 30,
        y: 12
    },

    bg: {
        x: { align: "center" },
        y: { align: "center" }
    },

    text: {
        x: { align: "center" },
        y: { align: "center" }
    }
};

// arrays are per resolution mode: vertical, square, horizontal
cleverapps.styles.YatzyProgressView = {
    position: [
        {
            x: { align: "center" },
            y: { align: "center", dy: 75 }
        }, {
            x: { align: "center" },
            y: { align: "center", dy: 45 }
        }, {
            x: { align: "center" },
            y: { align: "center", dy: 45 }
        }
    ],

    line: {
        overlap: -20,
        vertical: {
            height: 80,
            width: 40
        },
        horizontal: {
            height: 50,
            width: 100
        }
    },

    starCount: [{
        x: { align: "center" },
        y: { align: "top", dy: -20 }
    }, {
        x: { align: "center" },
        y: { align: "top", dy: -28 }
    }, {
        x: { align: "center" },
        y: { align: "top", dy: -28 }
    }],

    text: {
        width: 250,
        position: [{
            x: { align: "center" },
            y: { align: "bottom", dy: 20 }
        }, {
            x: { align: "center" },
            y: { align: "bottom", dy: -60 }
        }, {
            x: { align: "center" },
            y: { align: "bottom", dy: -60 }
        }]
    },

    background: {
        position: {
            frame1: [
                {
                    x: { align: "center", dx: 1 },
                    y: { align: "center", dy: 45 }
                }, {
                    x: { align: "center", dx: 1 },
                    y: { align: "center" }
                }, {
                    x: { align: "center", dx: 1 },
                    y: { align: "center" }
                }
            ],
            frame2: [
                {
                    x: { align: "center", dx: 1 },
                    y: { align: "center", dy: -332 }
                }, {
                    x: { align: "center", dx: 1 },
                    y: { align: "center", dy: -335 }
                }, {
                    x: { align: "center", dx: 1 },
                    y: { align: "center", dy: -335 }
                }
            ]
        },
        size: {
            frame1: [
                {
                    width: 365,
                    height: 660
                }, {
                    width: 1200,
                    height: 600
                }, {
                    width: 1200,
                    height: 600
                }
            ],
            frame2: [
                {
                    width: 290,
                    height: 79
                }, {
                    width: 434,
                    height: 70
                }, {
                    width: 434,
                    height: 70
                }
            ]
        }
    },

    scroll: {
        position: [{
            x: { align: "center" },
            y: { align: "center", dy: 20 }
        }, {
            x: { align: "center" },
            y: { align: "center", dy: -58 }
        }, {
            x: { align: "center" },
            y: { align: "center", dy: -58 }
        }],
        pagesCount: 3,
        zoom: 0.6,
        diffX: 20,
        dx: 8,

        vertical: {
            scale: 0.6,
            height: 900,
            width: 610,
            padding: 210
        },

        horizontal: {
            height: 600,
            width: 1200
        }
    }
};
